import { useEffect, useRef } from 'react';
import { signalInApp } from './signals';
import { isEngagementBlocked } from './eligibility';
import { isAppActive } from '../utils/appActivity';

// Accumulate foreground viewing time only. Repeated animation frames, hidden
// tabs, requests and filters never manufacture a meaningful view.
export default function useInAppView({ kind, storyId, title, ready, listRef, resetKey = '' }) {
  const latest = useRef({ title, ready });
  latest.current = { title, ready };
  useEffect(() => {
    if (!ready || !Number.isSafeInteger(storyId) || storyId <= 0) return undefined;
    let elapsed = 0, previous = Date.now(), sent = false, lastScroll = 0;
    const actors = new Set();
    const list = listRef?.current;
    const scroll = () => { lastScroll = Date.now(); };
    list?.addEventListener('scroll', scroll, { passive: true });
    const observer = kind === 'respondents' && list && typeof IntersectionObserver === 'function'
      ? new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (isAppActive() && entry.isIntersecting && entry.intersectionRatio >= .5) actors.add(entry.target.dataset.inAppActor);
        });
      }, { root: list, threshold: .5 }) : null;
    list?.querySelectorAll('[data-in-app-actor]').forEach(row => observer?.observe(row));
    const timer = setInterval(() => {
      const now = Date.now(), delta = Math.min(1000, now - previous);
      previous = now;
      if (sent || !latest.current.ready || isEngagementBlocked(window.location.pathname)) return;
      elapsed += delta;
      const enough = kind === 'statistics' ? elapsed >= 8000 : elapsed >= 30000 && actors.size >= 5 && now - lastScroll >= 1200;
      if (!enough) return;
      sent = true;
      signalInApp(kind, { storyId, title: latest.current.title });
    }, 500);
    return () => { clearInterval(timer); observer?.disconnect(); list?.removeEventListener('scroll', scroll); };
  }, [kind, storyId, ready, listRef, resetKey]);
}
